import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ticket, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { supabase } from '@/lib/supabase/client';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useModalStore } from '@/lib/stores/modal-store';

interface TicketInfo {
  id: string;
  ticket_number: number;
  guest_email?: string;
  transfer_type?: 'transfer' | 'allocation';
  experience?: {
    title: string;
    start_date?: string;
    location?: any;
  };
  booking?: {
    user?: {
      id: string;
      full_name: string;
      email: string;
    };
  };
}

export default function ClaimTicket() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();
  const [ticket, setTicket] = useState<TicketInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid claim link');
      setLoading(false);
      return;
    }

    fetchTicketInfo();
  }, [token]);

  const fetchTicketInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          guest_email,
          transfer_type,
          claimed_by,
          experience:experiences!tickets_experience_id_fkey (
            title,
            start_date,
            location
          ),
          booking:bookings!tickets_booking_id_fkey (
            user:users!bookings_user_id_fkey (
              id,
              full_name,
              email
            )
          )
        `)
        .eq('claim_token', token)
        .single();

      if (error) throw error;
      
      if (!data) {
        setError('Ticket not found');
        return;
      }

      // Check if already claimed by someone else
      if (data.claimed_by && data.claimed_by !== user?.id) {
        setError('This ticket has already been claimed');
        return;
      }

      setTicket(data as any);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError('Invalid or expired claim link');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!ticket || !user) return;
    
    setClaiming(true);
    try {
      // Verify email matches
      if (ticket.guest_email && ticket.guest_email !== user.email) {
        setError(`This ticket is for ${ticket.guest_email}. Please sign in with that email.`);
        return;
      }

      const { error } = await supabase
        .from('tickets')
        .update({
          claimed_by: user.id,
          claimed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', ticket.id)
        .eq('claim_token', token); // Double check token

      if (error) throw error;

      // Redirect to bookings
      navigate('/bookings', { 
        state: { message: 'Ticket claimed successfully!' } 
      });
    } catch (err) {
      console.error('Error claiming ticket:', err);
      setError('Failed to claim ticket. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-earth-800 rounded-lg p-8 max-w-md w-full text-center"
        >
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-display text-sand-100 mb-2">Unable to Claim Ticket</h1>
          <p className="text-sand-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  const isTransfer = ticket?.transfer_type === 'transfer';
  const fromPerson = ticket?.booking?.user?.full_name || 'Someone';

  return (
    <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black pt-20">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="bg-earth-800 rounded-lg overflow-hidden">
            <div className={`p-6 ${isTransfer ? 'bg-blue-900/20' : 'bg-green-900/20'}`}>
              <div className="flex items-center justify-center mb-4">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  isTransfer ? 'bg-blue-500/20' : 'bg-green-500/20'
                }`}>
                  <Ticket className={`h-10 w-10 ${isTransfer ? 'text-blue-400' : 'text-green-400'}`} />
                </div>
              </div>
              
              <h1 className="text-2xl font-display text-center text-sand-100 mb-2">
                {isTransfer ? 'Ticket Transfer' : 'Ticket Allocation'}
              </h1>
              
              <p className="text-center text-sand-400">
                {fromPerson} has {isTransfer ? 'transferred' : 'allocated'} a ticket to you!
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-earth-700/50 rounded-lg p-4">
                <h2 className="text-xl font-display text-sand-100 mb-2">
                  {ticket?.experience?.title}
                </h2>
                <p className="text-sand-400 text-sm">
                  Ticket #{ticket?.ticket_number}
                </p>
                {ticket?.experience?.start_date && (
                  <p className="text-sand-400 text-sm mt-1">
                    {new Date(ticket.experience.start_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {ticket?.guest_email && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <p className="text-sm text-yellow-400">
                    This ticket is designated for: <strong>{ticket.guest_email}</strong>
                  </p>
                </div>
              )}

              {user ? (
                <>
                  {ticket?.guest_email && ticket.guest_email !== user.email ? (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <p className="text-sm text-red-400">
                        Please sign in with {ticket.guest_email} to claim this ticket.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          supabase.auth.signOut();
                          navigate('/');
                        }}
                        className="mt-2"
                      >
                        Sign Out & Switch Account
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="lg"
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full"
                    >
                      {claiming ? (
                        <>Claiming...</>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Claim Your Ticket
                        </>
                      )}
                    </Button>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-center text-sand-400 text-sm">
                    Sign in or create an account to claim your ticket
                  </p>
                  <Button
                    size="lg"
                    onClick={() => openAuthModal('sign-in')}
                    className="w-full"
                  >
                    Sign In to Claim
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => openAuthModal('sign-up')}
                    className="w-full"
                  >
                    Create Account
                  </Button>
                </div>
              )}
            </div>
          </div>

          {!isTransfer && (
            <p className="text-center text-sand-500 text-sm mt-4">
              Note: This is an allocated ticket. The original purchaser retains ownership and can reclaim it at any time.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
} 